/**
 * Video Recorder Utility
 *
 * Wraps the browser MediaRecorder API with pluggable upload backends.
 * Defaults to Supabase Storage chunked uploads; accepts a custom
 * `uploadHandler(blob, sessionId, roundLabel, onProgress)` for other
 * providers (e.g. Google Drive).
 *
 * Usage (Supabase, default):
 *   const recorder = new VideoRecorder({ onError });
 *
 * Usage (Google Drive):
 *   const recorder = new VideoRecorder({
 *     uploadHandler: (blob, sessionId, roundLabel, onProgress) =>
 *       authorizeAndUpload(blob, sessionId, roundLabel, onProgress),
 *   });
 *
 *   await recorder.requestPermissions();
 *   await recorder.start(sessionId);
 *   const { videoUrl, duration } = await recorder.stop();
 */

import supabase from './supabase';

const CHUNK_INTERVAL_MS = 10_000;   // Upload a chunk every 10 seconds
const STORAGE_BUCKET    = 'interview-videos';
const MAX_VIDEO_MS      = 15 * 60 * 1000; // 15-minute hard cap

/** Preferred MIME types (ordered by quality/compat) */
const PREFERRED_MIMES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

function getSupportedMimeType() {
  for (const mime of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

/**
 * Convert a user's full name into a safe storage folder name.
 * e.g. "Jane Doe"  → "jane-doe"
 *      "María José" → "maria-jose"
 * Falls back to the raw string (e.g. UUID) if nothing valid remains.
 */
function sanitizeName(name) {
  if (!name) return 'unknown';
  return name
    .toLowerCase()
    .normalize('NFD')                       // decompose accented chars
    .replace(/[\u0300-\u036f]/g, '')        // strip accent marks
    .replace(/[^a-z0-9]+/g, '-')           // non-alphanumeric → hyphen
    .replace(/^-+|-+$/g, '')               // trim leading/trailing hyphens
    || 'unknown';
}

export function isBrowserSupported() {
  return (
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

export class VideoRecorder {
  /**
   * @param {object} opts
   * @param {function|null} opts.uploadHandler  - Custom upload function:
   *   (blob: Blob, sessionId: string, roundLabel: string, onProgress: function) => Promise<{ videoUrl: string }>
   *   When provided, Supabase chunk uploads are disabled and this function
   *   is called once with the final blob after recording stops.
   * @param {function|null} opts.onChunkUploaded - Supabase-mode chunk callback
   * @param {function|null} opts.onError         - Error callback
   * @param {function|null} opts.onDurationUpdate - Seconds elapsed callback
   * @param {function|null} opts.onUploadProgress - (loaded, total) upload progress callback
   */
  constructor({
    uploadHandler    = null,
    onChunkUploaded  = null,
    onError          = null,
    onDurationUpdate = null,
    onUploadProgress = null,
  } = {}) {
    this.uploadHandler    = uploadHandler;   // custom provider (e.g. Google Drive)
    this.onChunkUploaded  = onChunkUploaded;
    this.onError          = onError;
    this.onDurationUpdate = onDurationUpdate;
    this.onUploadProgress = onUploadProgress;

    this._stream          = null;
    this._recorder        = null;
    this._chunks          = [];
    this._chunkIndex      = 0;
    this._sessionId       = null;
    this._userId          = null;
    this._startTime       = null;
    this._durationTimer   = null;
    this._maxTimer        = null;
    this._mimeType        = '';
    this._uploadQueue     = Promise.resolve();
    this._allChunkUrls    = [];
    this._stopped         = false;
    this._finalBlob       = null;
  }

  /**
   * Request camera + microphone permissions and return the live preview stream.
   * @returns {Promise<MediaStream>}
   */
  async requestPermissions() {
    if (!isBrowserSupported()) {
      throw new Error(
        'Your browser does not support video recording. Please use Chrome, Edge, or Firefox.'
      );
    }

    // NOTE: Do NOT call enumerateDevices() before getUserMedia().
    // Before permission is granted, browsers may return an empty device list
    // which would incorrectly block the permission prompt from appearing.
    // getUserMedia() is the call that triggers the browser permission dialog.

    // Tier 1: ideal quality (no facingMode — desktop cameras don't support it)
    const constraintTiers = [
      {
        video: {
          width:     { ideal: 1280 },
          height:    { ideal: 720  },
          frameRate: { ideal: 30   },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate:       44100,
        },
      },
      // Tier 2: relaxed quality
      {
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      },
      // Tier 3: any video + any audio
      { video: true, audio: true },
    ];

    let lastErr;
    for (const constraints of constraintTiers) {
      try {
        this._stream = await navigator.mediaDevices.getUserMedia(constraints);
        return this._stream;
      } catch (err) {
        lastErr = err;
        // Don't retry on permission denial — that won't change with looser constraints
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') break;
      }
    }

    // getUserMedia failed — use enumerateDevices() as a diagnostic now that
    // the permission prompt has been shown (or denied).
    const err = lastErr;
    console.error('[VideoRecorder] getUserMedia failed across all constraint tiers:', {
      errorName: err.name,
      errorMessage: err.message,
      constraints: constraintTiers.map((c, i) => `Tier ${i + 1}`),
    });
    let msg;

    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      msg = 'Camera and microphone access was denied. Please click the camera icon in your browser address bar and allow access, then try again.';
    } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      // Double-check with enumerateDevices for a more specific diagnostic
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideo = devices.some(d => d.kind === 'videoinput');
        const hasAudio = devices.some(d => d.kind === 'audioinput');
        if (!hasVideo && !hasAudio) {
          msg = 'No camera or microphone was detected on this device. Please connect a device and try again.';
        } else if (!hasVideo) {
          msg = 'No camera was found but a microphone is available. Please connect a camera and try again.';
        } else {
          msg = 'No microphone was found but a camera is available. Please connect a microphone and try again.';
        }
      } catch {
        msg = 'No camera or microphone was found. Please connect a device and try again.';
      }
    } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
      msg = 'Your camera or microphone is in use by another application. Please close other apps using the camera and try again.';
    } else if (err.name === 'OverconstrainedError') {
      msg = 'Your camera does not meet the required quality. Please try a different camera.';
    } else {
      msg = `Could not access camera/microphone: ${err.message}`;
    }

    throw new Error(msg);
  }

  /**
   * Start recording a response.
   * @param {string} sessionId   - Interview session ID (used for storage path)
   * @param {string} userId      - VA user ID (kept for reference; no longer used as folder)
   * @param {number} roundNumber - Current interview round (1-5)
   * @param {string} [userName]  - VA full name; used as the top-level storage folder.
   *                               Falls back to userId when not provided.
   */
  async start(sessionId, userId, roundNumber = 1, userName = null) {
    if (!this._stream) {
      throw new Error('Call requestPermissions() before start().');
    }

    this._sessionId   = sessionId;
    this._userId      = userId;
    this._userName    = sanitizeName(userName || userId);
    this._roundNumber = roundNumber;
    this._chunkIndex  = 0;
    this._chunks     = [];
    this._allChunkUrls = [];
    this._stopped    = false;
    this._finalBlob  = null;

    this._mimeType = getSupportedMimeType();

    const options = this._mimeType ? { mimeType: this._mimeType } : {};

    try {
      this._recorder = new MediaRecorder(this._stream, options);
    } catch {
      // Fallback: try without options
      this._recorder = new MediaRecorder(this._stream);
      this._mimeType = this._recorder.mimeType || 'video/webm';
    }

    this._recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this._chunks.push(e.data);
        // Only enqueue Supabase chunk uploads when no custom uploadHandler
        if (!this.uploadHandler) {
          this._enqueueChunkUpload(roundNumber);
        }
      }
    };

    this._recorder.onerror = (e) => {
      console.error('[VideoRecorder] MediaRecorder error:', e.error);
      this.onError?.(new Error(`Recording error: ${e.error?.message || 'Unknown error'}`));
    };

    this._startTime = Date.now();
    this._recorder.start(CHUNK_INTERVAL_MS);

    // Duration update timer
    this._durationTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - this._startTime) / 1000);
      this.onDurationUpdate?.(secs);
    }, 1000);

    // Hard cap
    this._maxTimer = setTimeout(() => {
      if (!this._stopped) {
        console.warn('[VideoRecorder] Max duration reached, auto-stopping.');
        this.stop();
      }
    }, MAX_VIDEO_MS);
  }

  /**
   * Stop recording and finalize the video.
   * @returns {Promise<{ videoUrl: string, duration: number, chunkCount: number }>}
   */
  async stop() {
    if (!this._recorder || this._stopped) {
      throw new Error('Recorder is not active.');
    }

    this._stopped = true;
    clearInterval(this._durationTimer);
    clearTimeout(this._maxTimer);

    const duration = Math.floor((Date.now() - this._startTime) / 1000);

    return new Promise((resolve, reject) => {
      this._recorder.onstop = async () => {
        try {
          this._finalBlob = new Blob(this._chunks, { type: this._mimeType || 'video/webm' });

          if (this.uploadHandler) {
            // ── Custom provider (e.g. Google Drive) ──────────────────────────
            const result = await this.uploadHandler(
              this._finalBlob,
              this._sessionId,
              'final',
              this.onUploadProgress
            );
            resolve({
              videoUrl:    result.videoUrl || result.fileUrl || null,
              storagePath: result.fileId || result.storagePath || null,
              duration,
              chunkCount:  0,
            });
          } else {
            // ── Supabase Storage (default) ───────────────────────────────────
            // Wait for any in-flight chunk uploads
            await this._uploadQueue;

            const roundNum  = this._roundNumber || 1;
            const finalPath = `${this._userName}/${this._sessionId}/r${roundNum}-final.webm`;
            const { error } = await supabase.storage
              .from(STORAGE_BUCKET)
              .upload(finalPath, this._finalBlob, {
                contentType: this._mimeType || 'video/webm',
                upsert:      true,
              });

            if (error) throw error;

            // 1-year TTL so URLs stored in the DB remain playable on public profiles
            const { data: signedData, error: signedError } = await supabase.storage
              .from(STORAGE_BUCKET)
              .createSignedUrl(finalPath, 31_536_000);

            if (signedError) throw signedError;

            resolve({
              videoUrl:    signedData.signedUrl,
              storagePath: finalPath,
              duration,
              chunkCount:  this._chunkIndex,
            });
          }
        } catch (err) {
          reject(err);
        }
      };

      this._recorder.stop();
    });
  }

  /**
   * Release camera/microphone tracks.
   */
  releaseStream() {
    if (this._stream) {
      this._stream.getTracks().forEach((track) => track.stop());
      this._stream = null;
    }
  }

  /**
   * Get a local object URL for preview (before upload).
   * @returns {string|null}
   */
  getLocalPreviewUrl() {
    if (!this._finalBlob) return null;
    return URL.createObjectURL(this._finalBlob);
  }

  // ----- Private -----

  _enqueueChunkUpload(roundNumber) {
    const chunkIdx  = this._chunkIndex++;
    // Capture only the latest chunk, not the entire cumulative array
    const latestChunk = this._chunks[this._chunks.length - 1];

    this._uploadQueue = this._uploadQueue.then(async () => {
      try {
        const blob = new Blob([latestChunk], { type: this._mimeType || 'video/webm' });
        const path = `${this._userName}/${this._sessionId}/r${roundNumber}-chunk-${chunkIdx}.webm`;

        const { error } = await supabase.storage
          .from(STORAGE_BUCKET)
          .upload(path, blob, { contentType: this._mimeType || 'video/webm', upsert: true });

        if (error) {
          console.warn('[VideoRecorder] Chunk upload failed:', error.message);
        } else {
          this._allChunkUrls.push(path);
          this.onChunkUploaded?.({ chunkIndex: chunkIdx, path });
        }
      } catch (err) {
        console.warn('[VideoRecorder] Chunk upload error:', err);
      }
    });
  }
}

export default VideoRecorder;
